import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SessionCookieService, TableService } from '@docpath/linces-angular-components';
import { HeaderAction, SearchMode, SearchOptionType, SearchRowInfo, SearchRowInfoSelect } from '@docpath/webcomponents';
import { faMagnifyingGlass, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { Constants } from 'src/app/constants/constants';

import { RoutingPath } from '../../../../constants/routing-path';
import { Priority } from '../../../configuration/alert-rules/models/alert-rule';
import { AlertResource, AlertStatus } from '../../models/alert';
import { MonitoringConstants } from '../../monitoring-constants';
import { MonitoringFormService } from '../../services/monitoring-form.service';
import { MonitoringHttpService } from '../../services/monitoring-http.service';

@Injectable({
    providedIn: 'root'
})
export class MonitoringHeaderService {
    private readonly i18nPrefix = 'monitoring';

    constructor(
        private readonly sessionCookieService: SessionCookieService,
        private readonly tableService: TableService,
        private readonly monitoringHttpService: MonitoringHttpService,
        private readonly monitoringFormService: MonitoringFormService,
        private readonly router: Router
    ) {}

    getRightActions(): HeaderAction[] {
        return [this.initSearchAction()];
    }

    updateRightActions(dgeId: number, rightActions: HeaderAction[]): HeaderAction[] {
        if (rightActions.length === 1) {
            rightActions.push(this.initAssignAlertRuleAction(dgeId));
        } else {
            rightActions[1] = this.initAssignAlertRuleAction(dgeId);
        }
        return rightActions;
    }

    getLeftActions(): HeaderAction[] {
        const actions = [];

        if (this.sessionCookieService.getUserLogged()?.role !== Constants.ROLE_NORMAL) {
            actions.push(this.initManageAlertsRulesAction());
        }

        return actions;
    }

    getSearchAction(actions: HeaderAction[]): HeaderAction {
        return actions[0];
    }

    initSearchAvailableOptions(): SearchRowInfo[] {
        const columnsConfig = this.tableService.getColumns(this.i18nPrefix);

        const filters = [
            new SearchRowInfo(true, MonitoringConstants.NAME, undefined, SearchOptionType.TEXT, SearchMode.exactly, true, true),
            new SearchRowInfoSelect(false, MonitoringConstants.RESOURCE, undefined, SearchMode.is, this.getResourceFilterOptions(), true),
            new SearchRowInfoSelect(false, MonitoringConstants.PRIORITY, undefined, SearchMode.is, this.getPriorityFilterOptions(), true),
            new SearchRowInfoSelect(false, MonitoringConstants.STATUS, undefined, SearchMode.is, this.getStatusFilterOptions(), true),
            new SearchRowInfo(false, MonitoringConstants.DATE, undefined, SearchOptionType.DATE_RANGE, SearchMode.from, undefined, true, undefined, true)
        ];

        filters.forEach(filter => {
            const columnConfig = columnsConfig.find(column => column.name === filter.key);

            if (columnConfig) {
                filter.visible = columnConfig.visible;
            }
        });

        return filters;
    }

    private getPriorityFilterOptions(): Map<string, string> {
        return new Map<string, string>([
            [`${this.i18nPrefix}.table.priority.option.critical`, Priority.Critical],
            [`${this.i18nPrefix}.table.priority.option.low`, Priority.Low],
            [`${this.i18nPrefix}.table.priority.option.medium`, Priority.Medium]
        ]);
    }

    private getResourceFilterOptions(): Map<string, string> {
        return new Map<string, string>([
            [`${this.i18nPrefix}.table.resource.option.cpu`, AlertResource.Cpu],
            [`${this.i18nPrefix}.table.resource.option.disk`, AlertResource.Disk],
            [`${this.i18nPrefix}.table.resource.option.java`, AlertResource.Java],
            [`${this.i18nPrefix}.table.resource.option.ram`, AlertResource.Ram]
        ]);
    }

    private getStatusFilterOptions(): Map<string, string> {
        return new Map<string, string>([
            [`${this.i18nPrefix}.table.status.option.fulfilled`, AlertStatus.Fulfilled],
            [`${this.i18nPrefix}.table.status.option.not-fulfilled`, AlertStatus.NotFulfilled]
        ]);
    }

    private initManageAlertsRulesAction(): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.manage-alerts-rules.label`);

        action.tooltip = `${this.i18nPrefix}.header.manage-alerts-rules.tooltip`;
        action.action = () => {
            this.router.navigate([`/${RoutingPath.PATH_CONFIGURATION}/${RoutingPath.PATH_ALERT_RULES}`]);
        };

        return action;
    }

    private initSearchAction(): HeaderAction {
        const action = new HeaderAction(`${this.i18nPrefix}.header.search.label`, { cssClass: 'btn btn-dpsecondary' });
        action.tooltip = `${this.i18nPrefix}.header.search.tooltip`;
        action.icon = faMagnifyingGlass;
        return action;
    }

    private initAssignAlertRuleAction(dgeId: number): HeaderAction {

        const action = new HeaderAction(`${this.i18nPrefix}.header.assign-alert-rule.label`);

        action.icon = faPlusCircle;
        action.tooltip = `${this.i18nPrefix}.header.assign-alert-rule.tooltip`;

        action.action = () => {
            this.monitoringHttpService.getAssignedAlertRules(dgeId).subscribe(assignedAlertRules => {
                this.monitoringFormService.openAssignAlertRuleForm(dgeId, assignedAlertRules);
            });
        };

        return action;
    }
}
